import { baseApi } from "./baseApi";
import * as API from "@utils/apiPath";
import type { ApiResponse } from "interface/fuelType";
import type {
  VehicleCatalogColorRow,
  VehicleCatalogListResponse,
  VehicleCatalogMakeRow,
  VehicleCatalogModelRow,
  VehicleCatalogQueryParams,
  VehicleCatalogYearRow,
} from "interface/vehicleCatalog";

export const vehicleCatalogApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getVehicleCatalogMakes: builder.query<
      VehicleCatalogListResponse<VehicleCatalogMakeRow>,
      VehicleCatalogQueryParams
    >({
      query: (params) => ({
        url: `${API.GET_VEHICLE_CATALOG}/makes`,
        method: "GET",
        params,
      }),
      providesTags: ["VehicleCatalog"],
    }),
    getVehicleCatalogModels: builder.query<
      VehicleCatalogListResponse<VehicleCatalogModelRow>,
      { makeId?: string } & VehicleCatalogQueryParams
    >({
      query: (arg) => {
        const { makeId, ...params } = arg;
        if (makeId) {
          return {
            url: `${API.GET_VEHICLE_CATALOG}/models/${makeId}`,
            method: "GET",
            params,
          };
        }
        return {
          url: `${API.GET_VEHICLE_CATALOG}/models`,
          method: "GET",
          params,
        };
      },
      providesTags: ["VehicleCatalog"],
    }),
    getVehicleCatalogYears: builder.query<
      VehicleCatalogListResponse<VehicleCatalogYearRow>,
      { modelId?: string } & VehicleCatalogQueryParams
    >({
      query: (arg) => {
        const { modelId, ...params } = arg;
        if (modelId && String(modelId).trim() !== "") {
          return {
            url: `${API.GET_VEHICLE_CATALOG}/years/${modelId}`,
            method: "GET",
            params,
          };
        }
        return {
          url: `${API.GET_VEHICLE_CATALOG}/years`,
          method: "GET",
          params,
        };
      },
      providesTags: ["VehicleCatalog"],
    }),
    getVehicleCatalogColors: builder.query<
      VehicleCatalogListResponse<VehicleCatalogColorRow>,
      { yearId?: string } & VehicleCatalogQueryParams
    >({
      query: (arg) => {
        const { yearId, ...params } = arg;
        if (yearId && String(yearId).trim() !== "") {
          return {
            url: `${API.GET_VEHICLE_CATALOG}/colors/${yearId}`,
            method: "GET",
            params,
          };
        }
        return {
          url: `${API.GET_VEHICLE_CATALOG}/colors`,
          method: "GET",
          params,
        };
      },
      providesTags: ["VehicleCatalog"],
    }),
    updateVehicleCatalogMakeStatus: builder.mutation<
      ApiResponse,
      { id: string; status: boolean }
    >({
      query: ({ id, status }) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/makes/status/${id}`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    bulkVehicleCatalogMakeStatus: builder.mutation<
      ApiResponse,
      { ids: string[]; status: boolean }
    >({
      query: (body) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/makes/bulk-status`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    updateVehicleCatalogModelStatus: builder.mutation<
      ApiResponse,
      { id: string; status: boolean }
    >({
      query: ({ id, status }) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/models/status/${id}`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    bulkVehicleCatalogModelStatus: builder.mutation<
      ApiResponse,
      { ids: string[]; status: boolean }
    >({
      query: (body) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/models/bulk-status`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    updateVehicleCatalogYearStatus: builder.mutation<
      ApiResponse,
      { id: string; status: boolean }
    >({
      query: ({ id, status }) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/years/status/${id}`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    bulkVehicleCatalogYearStatus: builder.mutation<
      ApiResponse,
      { ids: string[]; status: boolean }
    >({
      query: (body) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/years/bulk-status`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    updateVehicleCatalogColorStatus: builder.mutation<
      ApiResponse,
      { yearId: string; colorId: string; status: boolean }
    >({
      query: (body) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/colors/status`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    bulkVehicleCatalogColorStatus: builder.mutation<
      ApiResponse,
      { items: { yearId: string; colorId: string }[]; status: boolean }
    >({
      query: (body) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/colors/bulk-status`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    createVehicleCatalogMake: builder.mutation<ApiResponse, { title: string }>({
      query: (body) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/makes`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    updateVehicleCatalogMake: builder.mutation<
      ApiResponse,
      { id: string; title: string }
    >({
      query: ({ id, title }) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/makes/${id}`,
        method: "PUT",
        body: { title },
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    deleteVehicleCatalogMake: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/makes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    createVehicleCatalogModel: builder.mutation<
      ApiResponse,
      { makeId: string; title: string; id?: number }
    >({
      query: (body) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/models`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    updateVehicleCatalogModel: builder.mutation<
      ApiResponse,
      { id: string; title?: string; sourceId?: number }
    >({
      query: ({ id, title, sourceId }) => {
        const body: { title?: string; id?: number } = {};
        if (title !== undefined) body.title = title;
        if (sourceId !== undefined) body.id = sourceId;
        return {
          url: `/${API.GET_VEHICLE_CATALOG}/models/${id}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: ["VehicleCatalog"],
    }),
    deleteVehicleCatalogModel: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/models/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    createVehicleCatalogYear: builder.mutation<
      ApiResponse,
      { modelId: string; year: number }
    >({
      query: (body) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/years`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    updateVehicleCatalogYear: builder.mutation<
      ApiResponse,
      { id: string; year: number }
    >({
      query: ({ id, year }) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/years/${id}`,
        method: "PUT",
        body: { year },
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    deleteVehicleCatalogYear: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/years/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    createVehicleCatalogColor: builder.mutation<
      ApiResponse,
      { yearId: string; name: string; rgb: string }
    >({
      query: (body) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/colors`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    updateVehicleCatalogColorItem: builder.mutation<
      ApiResponse,
      { yearId: string; colorId: string; name: string; rgb: string }
    >({
      query: (body) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/colors/item`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
    deleteVehicleCatalogColorItem: builder.mutation<
      ApiResponse,
      { yearId: string; colorId: string }
    >({
      query: (body) => ({
        url: `/${API.GET_VEHICLE_CATALOG}/colors/item`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["VehicleCatalog"],
    }),
  }),
});

export const {
  useGetVehicleCatalogMakesQuery,
  useLazyGetVehicleCatalogMakesQuery,
  useGetVehicleCatalogModelsQuery,
  useLazyGetVehicleCatalogModelsQuery,
  useGetVehicleCatalogYearsQuery,
  useLazyGetVehicleCatalogYearsQuery,
  useGetVehicleCatalogColorsQuery,
  useUpdateVehicleCatalogMakeStatusMutation,
  useBulkVehicleCatalogMakeStatusMutation,
  useUpdateVehicleCatalogModelStatusMutation,
  useBulkVehicleCatalogModelStatusMutation,
  useUpdateVehicleCatalogYearStatusMutation,
  useBulkVehicleCatalogYearStatusMutation,
  useUpdateVehicleCatalogColorStatusMutation,
  useBulkVehicleCatalogColorStatusMutation,
  useCreateVehicleCatalogMakeMutation,
  useUpdateVehicleCatalogMakeMutation,
  useDeleteVehicleCatalogMakeMutation,
  useCreateVehicleCatalogModelMutation,
  useUpdateVehicleCatalogModelMutation,
  useDeleteVehicleCatalogModelMutation,
  useCreateVehicleCatalogYearMutation,
  useUpdateVehicleCatalogYearMutation,
  useDeleteVehicleCatalogYearMutation,
  useCreateVehicleCatalogColorMutation,
  useUpdateVehicleCatalogColorItemMutation,
  useDeleteVehicleCatalogColorItemMutation,
} = vehicleCatalogApi;
